import React, { useCallback } from 'react'
import { Box } from '../Box/Box'
import { Text, TextProps } from '../Text/Text'
import { BoxComponentProps } from '../Box/Box.types'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

export type Tab = {
  text: string
  value: string
}

export type TabsProps = {
  tabs: Tab[]
  value: string
  onChange: (value: string) => void
  className?: string
  tabStyle?: 'default' | 'chip' | 'flat'
  boldText?: boolean
  size?: 's' | 'm' | 'l'
}

const sizeToPaddingMap: {
  [key in TabsProps['size'] as string]: {
    paddingX: BoxComponentProps['paddingX']
    paddingY: BoxComponentProps['paddingY']
  }
} = {
  s: { paddingX: 's', paddingY: 'xs' },
  m: { paddingX: 'm', paddingY: 's' },
  l: { paddingX: 'l', paddingY: 'm' }
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  value,
  onChange,
  className,
  tabStyle = 'default',
  boldText = false,
  size = 'l'
}) => {
  const theme = useTheme()
  const fontWeight = boldText ? 'stronger' : 'regular'
  const { paddingX, paddingY } = sizeToPaddingMap[size]

  const tabStyles: Record<string, BoxComponentProps> = {
    default: {
      paddingX,
      paddingY,
      radius: 'xs',
      flexGrow: 1,
      width: `${(100 / tabs.length).toFixed(2)}%`
    },
    chip: {
      paddingX,
      paddingY,
      radius: 'l',
      marginRight: 's',
      marginBottom: 'xs'
    },
    flat: {
      paddingX: 's',
      paddingY: 'xs',
      marginRight: 's',
      marginBottom: 'xs'
    }
  }

  const tabClickHandler = useCallback(onChange, [onChange])

  return (
    <Box display="flex" flexWrap="wrap" className={className}>
      {tabs.map((tab) => {
        const isActive = value === tab.value
        const backgroundColor = isActive
          ? theme.tabs.bg.active
          : tabStyle === 'flat'
          ? undefined
          : theme.tabs.bg[tabStyle]
        const tone = isActive ? 'action' : 'light'

        return (
          <>
            <Box
              key={tab.value}
              {...tabStyles[tabStyle]}
              background={tabStyle === 'flat' ? undefined : backgroundColor}
              cursor="pointer"
              justifyContent="center"
              onClick={() => tabClickHandler(tab.value)}
            >
              <StyledText
                tone={tone}
                size={tabStyle === 'flat' ? 'bigger' : 'base'}
                align="center"
                weight={fontWeight}
                isActive={isActive}
                isFlat={tabStyle === 'flat'}
                theme={theme}
              >
                {tab.text}
              </StyledText>
            </Box>
          </>
        )
      })}
    </Box>
  )
}

type StyledTextProps = TextProps & {
  isActive?: boolean
  isFlat?: boolean
}

const StyledText = styled(Text)<StyledTextProps>`
  ${(props) =>
    props.isFlat
      ? `
  box-sizing: border-box;
  border-bottom: 1px solid transparent;
  padding-bottom: 24px;
    ${
      props.isActive
        ? `
    border-bottom: 1px solid ${props.theme.brand.action};
    `
        : ''
    }
  `
      : ''}
  &:hover {
    color: var(--button-hover--primary);
  }
`
